import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { StableInput, StableTextarea } from '../components/StableInput';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Plus, Building, Globe, Mail, Phone, User, Trash2, Edit2, Search,
  Loader2, ExternalLink, Sparkles, FileText, Palette, Download,
  ArrowRight, Target, Handshake, DollarSign, Gift, Calendar, Users,
  ChevronRight, CheckCircle2, Clock, MessageSquare, Zap, HelpCircle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PARTNERSHIP_TYPES = [
  { value: 'financial_sponsor', label: 'Financial Sponsor', icon: DollarSign, desc: 'Direct cash sponsorship for programs or events', difficulty: 'moderate' },
  { value: 'in_kind', label: 'In-Kind Donation', icon: Gift, desc: 'Products, services, or expertise donated at no cost', difficulty: 'easy' },
  { value: 'event_sponsor', label: 'Event Sponsor', icon: Calendar, desc: 'Sponsorship for a specific event or campaign', difficulty: 'easy' },
  { value: 'employee_engagement', label: 'Employee Engagement', icon: Users, desc: 'Volunteer days, skills-based pro bono, mentoring', difficulty: 'easy' },
  { value: 'cause_marketing', label: 'Cause Marketing', icon: Handshake, desc: 'Co-branded campaign where both orgs benefit', difficulty: 'advanced' },
  { value: 'multi_year', label: 'Multi-Year Partnership', icon: Target, desc: 'Long-term strategic alliance with shared goals', difficulty: 'advanced' },
];

const OUTREACH_STAGES = [
  { value: 'identified', label: 'Identified', color: 'bg-stone-100 text-stone-600' },
  { value: 'researched', label: 'Researched', color: 'bg-blue-100 text-blue-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-amber-100 text-amber-700' },
  { value: 'meeting', label: 'Meeting Set', color: 'bg-purple-100 text-purple-700' },
  { value: 'proposal_sent', label: 'Proposal Sent', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'partnered', label: 'Partnered!', color: 'bg-green-100 text-green-700' },
];

const DIFFICULTY_CONFIG = {
  easy: { label: 'Easy', color: 'bg-green-50 text-green-700 border-green-200', desc: 'Good first outreach' },
  moderate: { label: 'Moderate', color: 'bg-amber-50 text-amber-700 border-amber-200', desc: 'Requires relationship building' },
  advanced: { label: 'Advanced', color: 'bg-red-50 text-red-700 border-red-200', desc: 'Needs strong case + time' },
};

export default function Partners() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [scraping, setScraping] = useState({});
  const [generating, setGenerating] = useState({});
  const [proposalDialog, setProposalDialog] = useState({ open: false, content: '', partner: null });
  const [formData, setFormData] = useState({
    name: '', website_url: '', industry: '', contact_name: '', contact_email: '',
    contact_phone: '', notes: '', alignment_areas: '', partnership_type: '', outreach_stage: 'identified',
  });

  // AI suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [addFromSuggestion, setAddFromSuggestion] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchPartners();
  }, [user, authLoading, navigate]);

  const fetchPartners = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${API}/api/partners`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setPartners(await response.json());
    } catch (error) { console.error('Error fetching partners:', error); }
    finally { setLoading(false); }
  };

  const fetchSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/partners/suggest`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to get suggestions');
      }
    } catch (error) { toast.error('Failed to get AI suggestions'); }
    finally { setLoadingSuggestions(false); }
  };

  const addSuggestionToForm = (suggestion) => {
    setAddFromSuggestion(suggestion);
    setFormData({
      name: suggestion.name || '',
      website_url: suggestion.website_url || '',
      industry: suggestion.industry || '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      notes: `Approach: ${suggestion.approach_angle || ''}\nEstimated value: ${suggestion.estimated_value || 'TBD'}\nWhy they fit: ${suggestion.why_good_fit || ''}`,
      alignment_areas: suggestion.why_good_fit || '',
      partnership_type: suggestion.partnership_type || '',
      outreach_stage: 'identified',
    });
    setIsAddDialogOpen(true);
  };

  const resetForm = () => setFormData({ name: '', website_url: '', industry: '', contact_name: '', contact_email: '', contact_phone: '', notes: '', alignment_areas: '', partnership_type: '', outreach_stage: 'identified' });

  const handleAddPartner = async () => {
    if (!formData.name) { toast.error('Partner name is required'); return; }
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${API}/api/partners`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData),
      });
      if (response.ok) { toast.success('Partner added to your pipeline!'); setIsAddDialogOpen(false); resetForm(); fetchPartners(); }
      else { const error = await response.json(); toast.error(error.detail || 'Failed to add partner'); }
    } catch (error) { toast.error('Failed to add partner'); }
  };

  const handleUpdatePartner = async () => {
    if (!selectedPartner) return;
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${API}/api/partners/${selectedPartner.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData),
      });
      if (response.ok) { toast.success('Partner updated'); setIsEditDialogOpen(false); setSelectedPartner(null); resetForm(); fetchPartners(); }
    } catch (error) { toast.error('Failed to update partner'); }
  };

  const handleDeletePartner = async (partnerId) => {
    if (!confirm('Remove this partner from your pipeline?')) return;
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${API}/api/partners/${partnerId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) { toast.success('Partner removed'); fetchPartners(); }
    } catch (error) { console.error('Error deleting partner:', error); }
  };

  const handleScrapeWebsite = async (partnerId) => {
    setScraping(prev => ({ ...prev, [partnerId]: true }));
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${API}/api/partners/${partnerId}/scrape`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) { toast.success('Company analyzed! Proposal generation now uses their data.'); fetchPartners(); }
      else { const error = await response.json(); toast.error(error.detail || 'Failed to analyze'); }
    } catch (error) { toast.error('Failed to analyze website'); }
    finally { setScraping(prev => ({ ...prev, [partnerId]: false })); }
  };

  const openEditDialog = (partner) => {
    setSelectedPartner(partner);
    setFormData({
      name: partner.name || '', website_url: partner.website_url || '', industry: partner.industry || '',
      contact_name: partner.contact_name || '', contact_email: partner.contact_email || '',
      contact_phone: partner.contact_phone || '', notes: partner.notes || '',
      alignment_areas: partner.alignment_areas || '', partnership_type: partner.partnership_type || '',
      outreach_stage: partner.outreach_stage || 'identified',
    });
    setIsEditDialogOpen(true);
  };

  const handleGenerateProposal = async (partner) => {
    setGenerating(prev => ({ ...prev, [partner.id]: true }));
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${API}/api/ai/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ generation_type: 'sponsorship_proposal', partner_id: partner.id, tone: 'professional', length: 'medium' }),
      });
      if (response.ok) { const data = await response.json(); setProposalDialog({ open: true, content: data.content, partner }); toast.success('Outreach message generated!'); }
      else { const error = await response.json(); toast.error(error.detail || 'Failed to generate'); }
    } catch (error) { toast.error('Failed to generate proposal'); }
    finally { setGenerating(prev => ({ ...prev, [partner.id]: false })); }
  };

  const handleDownloadProposal = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/grants/download-loi`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ content: proposalDialog.content }) });
      if (res.ok) { const blob = await res.blob(); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `proposal-${proposalDialog.partner?.name?.replace(/\s+/g, '-').toLowerCase() || 'partner'}.docx`; a.click(); URL.revokeObjectURL(url); toast.success('Downloaded as .docx'); }
    } catch (error) { toast.error('Download failed'); }
  };

  const handleEditInDesignStudio = () => {
    setProposalDialog(prev => ({ ...prev, open: false }));
    navigate('/design-studio', { state: { importedContent: proposalDialog.content, contentType: 'sponsorship_proposal', contentTitle: `Proposal for ${proposalDialog.partner?.name || 'Partner'}` } });
  };

  const getNextAction = (partner) => {
    const stage = partner.outreach_stage || 'identified';
    switch (stage) {
      case 'identified': return { text: 'Research their CSR priorities', action: 'Analyze Website', icon: Search };
      case 'researched': return { text: 'Send initial outreach email', action: 'Generate Message', icon: MessageSquare };
      case 'contacted': return { text: 'Follow up or schedule meeting', action: 'Write Follow-Up', icon: ArrowRight };
      case 'meeting': return { text: 'Prepare partnership proposal', action: 'Generate Proposal', icon: FileText };
      case 'proposal_sent': return { text: 'Follow up on proposal', action: 'Write Follow-Up', icon: Clock };
      case 'partnered': return { text: 'Plan stewardship & reporting', action: 'Create Report', icon: CheckCircle2 };
      default: return { text: 'Start with research', action: 'Analyze Website', icon: Search };
    }
  };

  if (loading) return <div className="min-h-screen pt-24 pb-16 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen pt-24 pb-16 bg-alabaster" data-testid="partners-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-1">
              Corporate Partnership Builder
            </h1>
            <p className="text-sm text-[#5C6B67]">
              Find, approach, and win corporate partnerships — step by step.
            </p>
          </div>
          <div className="flex gap-2">
            {suggestions.length === 0 && (
              <Button onClick={fetchSuggestions} disabled={loadingSuggestions} variant="outline" className="border-primary text-primary hover:bg-primary-50" data-testid="suggest-companies-btn">
                {loadingSuggestions ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Suggest Companies
              </Button>
            )}
            <Button onClick={() => { resetForm(); setAddFromSuggestion(null); setIsAddDialogOpen(true); }} className="bg-primary hover:bg-primary-800 text-white" data-testid="add-partner-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Company
            </Button>
          </div>
        </div>

        {/* AI Suggested Companies (available for all states) */}
        {suggestions.length > 0 && partners.length > 0 && (
          <div className="mb-6" data-testid="suggestions-section">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-heading font-bold text-base text-[#0A0A0A]">Suggested Companies</h3>
                <p className="text-xs text-[#5C6B67]">Based on your mission. Click to add to your pipeline.</p>
              </div>
              <Button variant="ghost" size="sm" className="text-stone-400" onClick={() => setSuggestions([])}>Dismiss</Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {suggestions.map((s, i) => {
                const diff = DIFFICULTY_CONFIG[s.difficulty] || DIFFICULTY_CONFIG.moderate;
                return (
                  <Card key={i} className="bg-white border-stone-200 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer" onClick={() => addSuggestionToForm(s)} data-testid={`suggestion-card-${i}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-xs text-[#0A0A0A] truncate">{s.name}</h4>
                        <Badge className={`text-[8px] border ${diff.color}`}>{diff.label}</Badge>
                      </div>
                      <p className="text-[10px] text-[#5C6B67] line-clamp-2 mb-2">{s.why_good_fit}</p>
                      <Button size="sm" className="w-full bg-primary/10 text-primary hover:bg-primary/20 text-[10px] h-6">
                        <Plus className="w-3 h-3 mr-0.5" /> Add
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty State — Guided Education */}
        {partners.length === 0 ? (
          <div className="space-y-6">
            {/* Partnership Types Education */}
            <Card className="bg-white border-stone-200">
              <CardContent className="py-10 px-8 text-center">
                <div className="w-16 h-16 bg-primary/5 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <Handshake className="w-8 h-8 text-primary" />
                </div>
                <h2 className="font-heading text-xl font-bold text-[#0A0A0A] mb-2">Build Your First Corporate Partnership</h2>
                <p className="text-[#5C6B67] max-w-lg mx-auto mb-8">
                  Corporate partnerships aren't just about cash. Companies partner with nonprofits for employee engagement, 
                  brand alignment, and community impact. Here's how to get started:
                </p>

                {/* Step-by-step guide */}
                <div className="grid sm:grid-cols-3 gap-4 text-left max-w-3xl mx-auto mb-8">
                  {[
                    { step: '1', title: 'Pick a local company', desc: 'Start with businesses in your area that align with your mission. Think: banks, law firms, restaurants, tech companies.' },
                    { step: '2', title: 'Choose your ask', desc: 'Sponsorship? Volunteers? Donated services? Match the ask to what the company can easily say yes to.' },
                    { step: '3', title: 'Reach out personally', desc: 'We\'ll generate a tailored outreach message based on your org profile and the company\'s priorities.' },
                  ].map(s => (
                    <div key={s.step} className="bg-stone-50 rounded-xl p-5 border border-stone-200">
                      <span className="text-xs text-[#00A859] font-bold uppercase">Step {s.step}</span>
                      <h4 className="font-semibold text-[#0A0A0A] text-sm mt-1 mb-1">{s.title}</h4>
                      <p className="text-xs text-[#5C6B67] leading-relaxed">{s.desc}</p>
                    </div>
                  ))}
                </div>

                <Button onClick={() => { resetForm(); setAddFromSuggestion(null); setIsAddDialogOpen(true); }} className="bg-primary hover:bg-primary-800 text-white" data-testid="empty-add-partner-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Your First Company
                </Button>
                <span className="mx-2 text-stone-400">or</span>
                <Button onClick={fetchSuggestions} disabled={loadingSuggestions} variant="outline" className="border-primary text-primary hover:bg-primary-50" data-testid="empty-suggest-btn">
                  {loadingSuggestions ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Get AI Suggestions
                </Button>
                <p className="text-xs text-[#5C6B67] mt-3">Don't know where to start? <Link to="/consulting" className="text-primary hover:underline">Book a strategy call</Link> and we'll help you identify prospects.</p>
              </CardContent>
            </Card>

            {/* AI Suggested Companies */}
            {suggestions.length > 0 && (
              <div data-testid="suggestions-section">
                <h3 className="font-heading font-bold text-lg text-[#0A0A0A] mb-1">Suggested Companies for You</h3>
                <p className="text-sm text-[#5C6B67] mb-3">Based on your organization's mission and focus. Click to add to your pipeline.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {suggestions.map((s, i) => {
                    const diff = DIFFICULTY_CONFIG[s.difficulty] || DIFFICULTY_CONFIG.moderate;
                    const typeInfo = PARTNERSHIP_TYPES.find(pt => pt.value === s.partnership_type);
                    return (
                      <Card key={i} className="bg-white border-stone-200 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer" onClick={() => addSuggestionToForm(s)} data-testid={`suggestion-card-${i}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-sm text-[#0A0A0A]">{s.name}</h4>
                              <p className="text-xs text-[#5C6B67]">{s.industry}</p>
                            </div>
                            <Badge className={`text-[9px] border ${diff.color}`}>{diff.label}</Badge>
                          </div>
                          <p className="text-xs text-[#5C6B67] mb-2 line-clamp-2">{s.why_good_fit}</p>
                          <div className="flex items-center gap-2 mb-2">
                            {typeInfo && <Badge variant="outline" className="text-[10px]">{typeInfo.label}</Badge>}
                            {s.estimated_value && <span className="text-[10px] text-green-700 font-medium">{s.estimated_value}</span>}
                          </div>
                          <div className="pt-2 border-t border-stone-100">
                            <p className="text-[10px] text-primary font-medium flex items-center gap-1">
                              <Zap className="w-3 h-3" /> {s.approach_angle?.slice(0, 80)}{s.approach_angle?.length > 80 ? '...' : ''}
                            </p>
                          </div>
                          <Button size="sm" className="w-full mt-3 bg-primary/10 text-primary hover:bg-primary/20 text-xs h-8" data-testid={`add-suggestion-${i}`}>
                            <Plus className="w-3 h-3 mr-1" /> Add to Pipeline
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Partnership Type Cards */}
            <div>
              <h3 className="font-heading font-bold text-lg text-[#0A0A0A] mb-3">Types of Corporate Partnerships</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {PARTNERSHIP_TYPES.map(pt => (
                  <div key={pt.value} className="bg-white border border-stone-200 rounded-xl p-4 hover:shadow-sm transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 bg-primary/5 rounded-lg flex items-center justify-center">
                        <pt.icon className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-[#0A0A0A]">{pt.label}</p>
                        <Badge className={`text-[9px] border ${DIFFICULTY_CONFIG[pt.difficulty].color}`}>{DIFFICULTY_CONFIG[pt.difficulty].label}</Badge>
                      </div>
                    </div>
                    <p className="text-xs text-[#5C6B67]">{pt.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* ===== PARTNERS LIST WITH PIPELINE VIEW ===== */
          <div className="space-y-4">
            {/* Pipeline summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
              {OUTREACH_STAGES.map(stage => {
                const count = partners.filter(p => (p.outreach_stage || 'identified') === stage.value).length;
                return (
                  <div key={stage.value} className={`text-center p-2.5 rounded-lg ${count > 0 ? stage.color : 'bg-stone-50 text-stone-400'}`}>
                    <p className="text-lg font-bold">{count}</p>
                    <p className="text-[10px] font-medium">{stage.label}</p>
                  </div>
                );
              })}
            </div>

            {/* Partner Cards */}
            {partners.map((partner) => {
              const partnerType = PARTNERSHIP_TYPES.find(pt => pt.value === partner.partnership_type);
              const stage = OUTREACH_STAGES.find(s => s.value === (partner.outreach_stage || 'identified'));
              const nextAction = getNextAction(partner);
              const NextIcon = nextAction.icon;
              const difficulty = partnerType ? DIFFICULTY_CONFIG[partnerType.difficulty] : null;

              return (
                <Card key={partner.id} className="bg-white border-stone-200 hover:shadow-md transition-shadow" data-testid={`partner-card-${partner.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Header row */}
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg text-[#0A0A0A]">{partner.name}</h3>
                          {stage && <Badge className={`text-xs ${stage.color}`}>{stage.label}</Badge>}
                          {partnerType && <Badge variant="outline" className="text-xs">{partnerType.label}</Badge>}
                          {difficulty && <Badge className={`text-[9px] border ${difficulty.color}`}>{difficulty.label}</Badge>}
                          {partner.scraped_data && !partner.scraped_data.error && (
                            <Badge className="bg-green-100 text-green-700 text-xs"><Sparkles className="w-3 h-3 mr-1" />Analyzed</Badge>
                          )}
                        </div>

                        {/* Contact info */}
                        <div className="flex flex-wrap gap-3 text-sm text-[#5C6B67] mb-2">
                          {partner.website_url && (
                            <a href={partner.website_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-primary">
                              <Globe className="w-3 h-3" />{partner.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '').slice(0, 30)}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                          {partner.contact_name && <span className="flex items-center gap-1"><User className="w-3 h-3" />{partner.contact_name}</span>}
                          {partner.contact_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{partner.contact_email}</span>}
                        </div>

                        {partner.alignment_areas && <p className="text-xs text-[#5C6B67] mb-2"><span className="font-medium text-[#0A0A0A]">Alignment:</span> {partner.alignment_areas}</p>}

                        {/* Scraped data + actions */}
                        {partner.scraped_data && !partner.scraped_data.error && (
                          <div className="mt-3 p-3 bg-stone-50 rounded-lg">
                            <p className="text-xs text-[#5C6B67] line-clamp-2 mb-3">
                              {partner.scraped_data.description || partner.scraped_data.content_preview?.substring(0, 150)}...
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <Button size="sm" onClick={() => handleGenerateProposal(partner)} disabled={generating[partner.id]} className="bg-primary hover:bg-primary-800 text-white" data-testid={`generate-proposal-${partner.id}`}>
                                {generating[partner.id] ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                                Generate Outreach
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => navigate('/ai-generator')} data-testid={`more-content-${partner.id}`}>
                                <FileText className="w-4 h-4 mr-1" /> More Types
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Next Action Prompt */}
                        <div className="mt-3 flex items-center gap-2 p-2.5 bg-primary/5 rounded-lg border border-primary/10">
                          <NextIcon className="w-4 h-4 text-primary flex-shrink-0" />
                          <p className="text-xs text-[#0A0A0A]"><span className="font-medium">Next:</span> {nextAction.text}</p>
                          <Button size="sm" variant="ghost" className="ml-auto text-primary text-xs h-7 px-2"
                            onClick={() => {
                              if (nextAction.action === 'Analyze Website' && partner.website_url) handleScrapeWebsite(partner.id);
                              else if (nextAction.action === 'Generate Message' || nextAction.action === 'Generate Proposal') handleGenerateProposal(partner);
                              else navigate('/ai-generator');
                            }}
                            data-testid={`next-action-${partner.id}`}
                          >
                            {nextAction.action} <ChevronRight className="w-3 h-3 ml-0.5" />
                          </Button>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        {partner.website_url && !partner.scraped_data && (
                          <Button variant="outline" size="sm" onClick={() => handleScrapeWebsite(partner.id)} disabled={scraping[partner.id]} className="text-primary border-primary/30 hover:bg-primary-50">
                            {scraping[partner.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" />Analyze</>}
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(partner)} className="text-stone-500 hover:text-primary"><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeletePartner(partner.id)} className="text-stone-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* ===== ADD PARTNER DIALOG (Enhanced) ===== */}
        <Dialog open={isAddDialogOpen} onOpenChange={(open) => { setIsAddDialogOpen(open); if (!open) setAddFromSuggestion(null); }}>
          <DialogContent className="max-w-lg bg-white max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">{addFromSuggestion ? `Add ${addFromSuggestion.name}` : 'Add a Corporate Partner'}</DialogTitle>
              <DialogDescription>
                {addFromSuggestion
                  ? 'We\'ve pre-filled what we know. Add contact details to start your outreach.'
                  : 'Add a company to your partnership pipeline. We\'ll help you with the next steps.'}
              </DialogDescription>
            </DialogHeader>

            {/* Suggestion context banner */}
            {addFromSuggestion && (
              <div className="bg-primary/5 border border-primary/10 rounded-lg p-3 mt-1">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-primary">Why this company?</p>
                    <p className="text-xs text-[#5C6B67] mt-0.5">{addFromSuggestion.why_good_fit}</p>
                    {addFromSuggestion.approach_angle && (
                      <p className="text-xs text-[#5C6B67] mt-1"><span className="font-medium">Suggested approach:</span> {addFromSuggestion.approach_angle}</p>
                    )}
                    {addFromSuggestion.estimated_value && (
                      <p className="text-xs text-green-700 font-medium mt-1">Estimated value: {addFromSuggestion.estimated_value}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-4 mt-2">
              <div>
                <Label>Company Name *</Label>
                <StableInput value={formData.name} onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="name" placeholder="e.g., First National Bank, Google, Local Law Firm" data-testid="partner-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Website URL</Label>
                  <StableInput value={formData.website_url} onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="website_url" placeholder="https://company.com" />
                  <p className="text-[10px] text-[#5C6B67] mt-0.5">We'll analyze their CSR priorities</p>
                </div>
                <div>
                  <Label>Industry</Label>
                  <StableInput value={formData.industry} onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="industry" placeholder="Finance, Tech, Retail..." />
                </div>
              </div>
              <div>
                <Label>Partnership Type</Label>
                <Select value={formData.partnership_type} onValueChange={(v) => setFormData(prev => ({ ...prev, partnership_type: v }))}>
                  <SelectTrigger className="mt-1" data-testid="partnership-type-select"><SelectValue placeholder="What kind of partnership?" /></SelectTrigger>
                  <SelectContent>
                    {PARTNERSHIP_TYPES.map(pt => <SelectItem key={pt.value} value={pt.value}>{pt.label} — {pt.desc}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>How does this company align with your mission?</Label>
                <StableInput value={formData.alignment_areas} onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="alignment_areas" placeholder="e.g., They support youth education, their employees volunteer locally..." />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Contact Name</Label><StableInput value={formData.contact_name} onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="contact_name" placeholder="Optional" /></div>
                <div><Label>Contact Email</Label><StableInput value={formData.contact_email} onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="contact_email" placeholder="Optional" /></div>
                <div><Label>Contact Phone</Label><StableInput value={formData.contact_phone} onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="contact_phone" placeholder="Optional" /></div>
              </div>
              <div>
                <Label>Notes</Label>
                <StableTextarea value={formData.notes} onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="notes" placeholder="How do you know about them? Any connections?" rows={2} />
              </div>
              <Button onClick={handleAddPartner} className="w-full bg-primary hover:bg-primary-800 text-white" data-testid="submit-partner-btn">
                Add to Pipeline <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== EDIT DIALOG ===== */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-lg bg-white max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Partner</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div><Label>Company Name *</Label><StableInput value={formData.name} onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="name" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Website URL</Label><StableInput value={formData.website_url} onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="website_url" /></div>
                <div><Label>Industry</Label><StableInput value={formData.industry} onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="industry" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Partnership Type</Label>
                  <Select value={formData.partnership_type} onValueChange={(v) => setFormData(prev => ({ ...prev, partnership_type: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>{PARTNERSHIP_TYPES.map(pt => <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Outreach Stage</Label>
                  <Select value={formData.outreach_stage} onValueChange={(v) => setFormData(prev => ({ ...prev, outreach_stage: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{OUTREACH_STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Alignment Areas</Label><StableInput value={formData.alignment_areas} onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="alignment_areas" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Contact</Label><StableInput value={formData.contact_name} onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="contact_name" /></div>
                <div><Label>Email</Label><StableInput value={formData.contact_email} onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="contact_email" /></div>
                <div><Label>Phone</Label><StableInput value={formData.contact_phone} onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="contact_phone" /></div>
              </div>
              <div><Label>Notes</Label><StableTextarea value={formData.notes} onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="notes" rows={2} /></div>
              <Button onClick={handleUpdatePartner} className="w-full bg-primary hover:bg-primary-800 text-white">Save Changes</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== PROPOSAL DIALOG ===== */}
        <Dialog open={proposalDialog.open} onOpenChange={(open) => setProposalDialog(prev => ({ ...prev, open }))}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col bg-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />Outreach for {proposalDialog.partner?.name}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto mt-4 bg-stone-50 rounded-lg p-6">
              <pre className="whitespace-pre-wrap text-sm text-stone-800 font-sans leading-relaxed" data-testid="proposal-content">{proposalDialog.content}</pre>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t">
              <p className="text-xs text-[#5C6B67]">Tailored using your org profile + company data</p>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(proposalDialog.content); toast.success('Copied!'); }} data-testid="copy-proposal-btn">Copy</Button>
                <Button variant="outline" size="sm" onClick={handleDownloadProposal} data-testid="download-proposal-btn"><Download className="w-4 h-4 mr-1" />.docx</Button>
                <Button size="sm" onClick={handleEditInDesignStudio} className="bg-primary hover:bg-primary-800 text-white" data-testid="proposal-to-design-btn"><Palette className="w-4 h-4 mr-1" />Design Studio</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
